import { getApiBaseUrl } from "./client";
import { ApiClientError } from "./types";

export const PLATFORM_ADMIN_KEY_STORAGE = "godo_platform_admin_key";

export function getStoredPlatformAdminKey(): string | null {
  try {
    return sessionStorage.getItem(PLATFORM_ADMIN_KEY_STORAGE)?.trim() || null;
  } catch {
    return null;
  }
}

export function setStoredPlatformAdminKey(key: string): void {
  sessionStorage.setItem(PLATFORM_ADMIN_KEY_STORAGE, key.trim());
}

export function clearStoredPlatformAdminKey(): void {
  sessionStorage.removeItem(PLATFORM_ADMIN_KEY_STORAGE);
}

async function platformFetch<T>(
  path: string,
  adminKey: string,
  init?: RequestInit,
): Promise<T> {
  const base = getApiBaseUrl();
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Platform-Admin-Key": adminKey,
      ...(init?.headers ?? {}),
    },
  });
  let payload: {
    ok?: boolean;
    error?: { message?: string; code?: number; reason?: string };
    data?: T;
  };
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const snippet = (await response.text()).replace(/\s+/g, " ").slice(0, 120);
    throw new ApiClientError(
      `الرد ليس JSON (تحقق من VITE_API_BASE_URL → api-pro). ${snippet}`,
      0,
      response.status,
      "bad_response",
    );
  }
  payload = (await response.json()) as typeof payload;
  if (!response.ok || !payload.ok) {
    throw new ApiClientError(
      payload.error?.message ?? `HTTP ${response.status}`,
      payload.error?.code ?? 0,
      response.status,
      payload.error?.reason,
    );
  }
  return payload.data as T;
}

export interface PlatformSummary {
  users: number;
  orgs: number;
  subscriptions: number;
  trialBlacklist: number;
}

export interface PlatformOrgRow {
  orgId: string;
  orgCode: string;
  legalName: string;
  orgStatus: number;
  createdAt: string;
  userCount: number;
  ownerEmail: string | null;
  subscription: {
    isActive: boolean;
    isTrial: boolean;
    isExpired: boolean;
    daysRemaining: number;
    trialEnds: string | null;
    paidUntil: string | null;
  };
}

export interface PlatformUserRow {
  userId: string;
  orgId: string;
  email: string;
  displayName: string;
  status: number;
  ownerFlag: number;
  lastLoginAt: string | null;
  whatsapp: string | null;
  orgName: string;
  orgCode: string;
}

export function fetchPlatformSummary(key: string) {
  return platformFetch<PlatformSummary>("/v1/platform-admin/summary", key);
}

export function fetchPlatformOrgs(key: string, limit = 50, offset = 0) {
  return platformFetch<{ items: PlatformOrgRow[]; limit: number; offset: number }>(
    `/v1/platform-admin/orgs?limit=${limit}&offset=${offset}`,
    key,
  );
}

export function fetchPlatformUsers(key: string, limit = 50, offset = 0, email = "") {
  const q = email.trim() ? `&email=${encodeURIComponent(email.trim())}` : "";
  return platformFetch<{ items: PlatformUserRow[]; limit: number; offset: number }>(
    `/v1/platform-admin/users?limit=${limit}&offset=${offset}${q}`,
    key,
  );
}

/** Mini Google Sheet parity — one row per user. */
export interface PlatformSheetRow {
  userId: string;
  orgId: string;
  orgCode: string;
  login: string;
  role: string;
  active: boolean;
  ownerFlag: boolean;
  paidUntil: string;
  trialEnds: string;
  companyName: string;
  displayName: string;
  address: string;
  whatsapp: string;
  altEmail: string;
  billCom: boolean;
  planFull: boolean;
  modInv: boolean;
  modAcc: boolean;
  modMkt: boolean;
  modHr: boolean;
  modFin: boolean;
  modSl: boolean;
  lastLoginAt: string | null;
  subscriptionKind: string;
  subscriptionLabel: string;
  daysRemaining: number;
  trialEmailRegistered: boolean;
}

export type PlatformSheetSaveBody = Partial<
  Pick<
    PlatformSheetRow,
    | "login"
    | "role"
    | "companyName"
    | "displayName"
    | "address"
    | "whatsapp"
    | "altEmail"
    | "billCom"
    | "planFull"
    | "modInv"
    | "modAcc"
    | "modMkt"
    | "modHr"
    | "modFin"
    | "modSl"
  >
> & {
  active?: boolean;
  newPassword?: string;
  paidUntil?: string | null;
  trialEnds?: string | null;
};

export function fetchPlatformSheet(key: string, limit = 500) {
  return platformFetch<{ rows: PlatformSheetRow[]; limit: number; offset: number }>(
    `/v1/platform-admin/sheet?limit=${limit}&offset=0`,
    key,
  );
}

export function savePlatformSheetRow(key: string, userId: string, body: PlatformSheetSaveBody) {
  return platformFetch<{ row: PlatformSheetRow }>(
    `/v1/platform-admin/sheet/${encodeURIComponent(userId)}`,
    key,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export function fetchPlatformOrgDetail(key: string, orgId: string) {
  return platformFetch<{
    org: { orgId: string; orgCode: string; legalName: string; status: number; createdAt: string };
    subscription: PlatformOrgRow["subscription"];
    users: Array<{
      userId: string;
      email: string;
      displayName: string;
      status: number;
      ownerFlag: number;
      lastLoginAt: string | null;
    }>;
  }>(`/v1/platform-admin/org/${encodeURIComponent(orgId)}`, key);
}
